import { useCallback, useEffect, useState } from 'react';
import { message } from 'antd';

export const PASSWORD_POLICY_SCOPES = {
  platformUser: 'PlatformUser',
  wpaAccount: 'WpaAccount',
};

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export async function fetchPasswordPolicy(scope, locale) {
  const params = new URLSearchParams();
  if (locale) params.set('locale', locale);
  const qs = params.toString();
  const res = await fetch(`/api/password-policy/${encodeURIComponent(scope)}${qs ? `?${qs}` : ''}`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  const json = await res.json();
  return json?.data || null;
}

export async function generatePasswordForScope(scope) {
  const res = await fetch(`/api/password-policy/${encodeURIComponent(scope)}/generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  const json = await res.json();
  return json?.data?.password || '';
}

export function buildPasswordFormRules(policy, options = {}) {
  const {
    required = false,
    requiredMessage = '請輸入密碼',
    skipWhenEmpty = false,
  } = options;
  const rules = [];
  if (required) {
    rules.push({ required: true, message: requiredMessage });
  }
  if (!policy) return rules;

  rules.push({
    validator: (_, value) => {
      if (skipWhenEmpty && (!value || !String(value).trim())) {
        return Promise.resolve();
      }
      if (!value || !String(value).trim()) {
        return Promise.resolve();
      }
      const text = String(value);
      if (policy.minLength && text.length < policy.minLength) {
        return Promise.reject(new Error(policy.message));
      }
      if (policy.maxLength && text.length > policy.maxLength) {
        return Promise.reject(new Error(policy.message));
      }
      if (policy.regex) {
        try {
          const re = new RegExp(policy.regex);
          if (!re.test(text)) {
            return Promise.reject(new Error(policy.message));
          }
        } catch {
          return Promise.reject(new Error(policy.message));
        }
      }
      return Promise.resolve();
    },
  });
  return rules;
}

export function usePasswordPolicy(scope, locale) {
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const reload = useCallback(async () => {
    if (!scope) return;
    setLoading(true);
    try {
      const data = await fetchPasswordPolicy(scope, locale);
      setPolicy(data);
    } catch (err) {
      console.error('[password-policy] load failed', err);
      setPolicy(null);
    } finally {
      setLoading(false);
    }
  }, [scope, locale]);

  useEffect(() => {
    reload();
  }, [reload]);

  const generatePassword = useCallback(async () => {
    if (!scope) return '';
    setGenerating(true);
    try {
      return await generatePasswordForScope(scope);
    } finally {
      setGenerating(false);
    }
  }, [scope]);

  return {
    policy,
    loading,
    generating,
    reload,
    generatePassword,
  };
}

export async function applyGeneratedPassword(form, scope, fieldNames = {}, labels = {}) {
  const passwordField = fieldNames.password || 'password';
  const confirmField = fieldNames.confirmPassword || 'confirmPassword';
  try {
    const password = await generatePasswordForScope(scope);
    if (!password) {
      message.error(labels.generateFailed || '無法產生密碼');
      return false;
    }
    const patch = { [passwordField]: password };
    if (confirmField) patch[confirmField] = password;
    form.setFieldsValue(patch);
    message.success(labels.generateSuccess || '已產生密碼');
    return true;
  } catch (err) {
    message.error(err.message || labels.generateFailed || '無法產生密碼');
    return false;
  }
}
